/**
 * Equities research pages: Track record (every issued plan's model outcome, plus
 * historical replays of the same rules) and Journal (the customer's actual fills).
 *
 * Model outcomes, replay results and actual fills are always labelled separately;
 * none of them is presented as a win probability.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Badge, Button, ConfirmModal, EmptyState, Icon, Modal, Panel, SegmentedControl, Skeleton, StatCell,
  Switch, TextField, Select, etSyd, fmt,
} from "@/sentinel";
import { SETUP_LABEL, PlanLifecycleModal, eqPoll, isNum, px } from "@/pages/Equities";

const fmtR = (v) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v)).toFixed(2)}R` : "—");
const fmtRate = (v) => (isNum(v) ? fmt(v * 100, "pct") : "—");

const BUCKET_LABEL = {
  volume_breakout: "Breakout", trend_pullback: "Pullback", earnings_drift: "Earnings drift",
  RISK_ON: "Risk-on", NEUTRAL: "Neutral", RISK_OFF: "Risk-off",
};

function SummaryCells({ s, testId }) {
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={testId}>
      <StatCell size="sm" label="Plans" value={String(s.plans ?? 0)} />
      <StatCell size="sm" label="Closed trades" value={String(s.closed ?? 0)}
                sub={s.open ? `${s.open} open` : null} />
      <StatCell size="sm" label="Trigger rate" value={fmtRate(s.trigger_rate)} />
      <StatCell size="sm" label="Win rate" value={fmtRate(s.win_rate)} />
      <StatCell size="sm" label="Avg R / trade" value={fmtR(s.mean_r)}
                sub={s.ci95 ? `95% CI ${fmtR(s.ci95[0])} to ${fmtR(s.ci95[1])}` : null} />
      <StatCell size="sm" label="Total R" value={fmtR(s.sum_r)} />
      <StatCell size="sm" label="Max drawdown" value={fmtR(s.max_dd_r)} />
      <StatCell size="sm" label="Reached target" value={fmtRate(s.pct_target)}
                sub={s.closed ? `${fmtRate(s.pct_stop)} stopped · ${fmtRate(s.pct_time)} timed out` : null} />
    </div>
  );
}

function BreakdownTable({ title, rows, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid={testId}>
      <span className="sn-micro">{title}</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label={title}>
          <thead><tr>
            <th>Bucket</th><th className="r">Plans</th><th className="r">Closed</th><th className="r">Win rate</th>
            <th className="r">Avg R</th><th className="r">95% CI</th><th className="r">Total R</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.bucket)}>
                <td>{BUCKET_LABEL[r.bucket] || String(r.bucket)}</td>
                <td className="num">{r.plans}</td>
                <td className="num">{r.closed}</td>
                <td className="num">{fmtRate(r.win_rate)}</td>
                <td className="num" style={{ color: "var(--text-primary)" }}>{fmtR(r.mean_r)}</td>
                <td className="num">{r.ci95 ? `${fmtR(r.ci95[0])} … ${fmtR(r.ci95[1])}` : "—"}</td>
                <td className="num">{fmtR(r.sum_r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const BREAKDOWNS = [
  ["by_setup", "By setup"], ["by_regime", "By market regime at issue"], ["by_actionability", "By desk status at issue"],
  ["by_rank", "By rank"], ["by_score", "By score"], ["by_rs_pct", "By momentum percentile"],
  ["by_rs20", "By 20-day relative strength (the v1 ranking)"], ["by_extension", "By distance above EMA20"],
  ["by_tightness", "By range contraction"], ["by_risk_atr", "By risk (ATR)"], ["by_target_atr", "By target distance (ATR)"],
  ["by_quality", "By fundamentals score (weight 0 until proven)"],
];

function Breakdowns({ data, prefix }) {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-4)" }}>
      {BREAKDOWNS.map(([k, t]) => <BreakdownTable key={k} title={t} rows={data?.[k]} testId={`${prefix}-${k}`} />)}
    </div>
  );
}

// ── live track record ────────────────────────────────────────────────────────
function LiveRecord() {
  const [version, setVersion] = useState("equities-swing-v2");
  const [historyId, setHistoryId] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["eq-track-record", version], queryFn: () => api.eqTrackRecord(version === "all" ? null : version),
    refetchInterval: eqPoll(120000),
  });
  const recent = data?.recent || [];
  return (
    <Panel title="Live track record" icon="History" data-testid="live-record"
           actions={
             <SegmentedControl label="Rule version" value={version} onChange={setVersion} size="sm"
                               options={[{ value: "equities-swing-v2", label: "v2" }, { value: "equities-swing-v1", label: "v1" },
                                         { value: "all", label: "All" }]} />
           }>
      {isLoading ? <Skeleton lines={4} /> : (
        <div className="sn-stack">
          <p className="sn-small sn-muted" style={{ margin: 0 }}>{data?.note}</p>
          <SummaryCells s={data?.overall} testId="live-summary" />
          {(data?.overall?.closed || 0) < 30 && (
            <p className="sn-small" style={{ margin: 0 }}>
              <Icon name="Info" size={14} /> Fewer than 30 closed trades — too few to judge the rules. Use the historical replay below for a larger sample.
            </p>
          )}
          <Breakdowns data={data} prefix="live" />
          {recent.length > 0 && (
            <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
              <span className="sn-micro">Recent plans</span>
              <div className="sn-table-wrap">
                <table className="sn-table" aria-label="Recent plans">
                  <thead><tr>
                    <th>Symbol</th><th>Setup</th><th>Signal</th><th>Status</th><th className="r">Entry</th>
                    <th className="r">Exit</th><th className="r">R</th><th className="r">Days</th><th />
                  </tr></thead>
                  <tbody>
                    {recent.map((r) => (
                      <tr key={r.plan_id} data-testid={`recent-${r.plan_id}`}>
                        <td className="id">{r.symbol}</td>
                        <td>{SETUP_LABEL[r.setup] || r.setup}</td>
                        <td className="num">{r.signal_date}</td>
                        <td>{r.status}{r.ambiguous ? " *" : ""}</td>
                        <td className="num">{r.entry_price ? px(r.entry_price) : "—"}</td>
                        <td className="num">{r.exit_price ? px(r.exit_price) : "—"}</td>
                        <td className="num" style={{ color: "var(--text-primary)" }}>{fmtR(r.r_multiple ?? r.unrealized_r)}</td>
                        <td className="num">{r.days_held ?? "—"}</td>
                        <td><Button size="sm" variant="ghost" icon="History" onClick={() => setHistoryId(r.plan_id)}>History</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <span className="sn-small sn-faint">* stop and target in the same daily bar — the stop is assumed.</span>
            </div>
          )}
        </div>
      )}
      {historyId && <PlanLifecycleModal planId={historyId} onClose={() => setHistoryId(null)} />}
    </Panel>
  );
}

// ── trend following, five funds (paper) ──────────────────────────────────────
// research/trend_following/RESULTS.md: the rule is fixed; the page shows the signals and a paper record.
const FUND_NAME = {
  SPY: "US stocks", EFA: "Foreign developed stocks", IEF: "7 to 10 year Treasuries", VNQ: "US REITs",
  DBC: "Commodities", BIL: "Treasury bills (cash)",
};
/** A return as a signed percent: +5.4%, −7.3%; `missing` when there is no number. */
const pctN = (v, d = 1, missing = "n/a") => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v) * 100).toFixed(d)}%` : missing);
const px2 = (v) => (isNum(v) ? Number(v).toFixed(2) : "n/a");
const trendErr = (e) => fmtErr(e.response?.data?.detail) || e.message;

function TrendSignals({ signals, asOf, source }) {
  const rows = Object.entries(signals || {});
  if (!rows.length) {
    return (
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="trend-signals">
        No signals yet. They are computed from the month-end closes when the nightly job runs; an owner can run it now.
      </p>
    );
  }
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="trend-signals">
      <span className="sn-micro">
        Signals at the {asOf} month-end{source === "preview" ? " (preview; no paper month holds them yet)" : ""}
      </span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="Trend signals">
          <thead><tr>
            <th>Fund</th><th>What it holds</th><th className="r">Month-end close</th><th className="r">Ten-month average</th><th>Position</th>
          </tr></thead>
          <tbody>
            {rows.map(([sym, s]) => (
              <tr key={sym} data-testid={`trend-signal-${sym}`}>
                <td className="id">{sym}</td>
                <td className="sn-small">{FUND_NAME[sym] || ""}</td>
                <td className="num">{s ? px2(s.close) : "n/a"}</td>
                <td className="num">{s ? px2(s.sma10) : "n/a"}</td>
                <td>{s ? (s.hold ? <Badge tone="brand">Hold</Badge> : <Badge>Cash</Badge>) : <span className="sn-faint">no data</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrendMonths({ months }) {
  if (!months.length) return null;
  return (
    <div className="sn-table-wrap" data-testid="trend-months">
      <table className="sn-table" aria-label="Trend paper months">
        <thead><tr>
          <th>Entry</th><th>Exit</th><th>Held</th><th className="r">In cash</th><th className="r">Rule net</th>
          <th className="r">Benchmark net</th><th className="r">Excess</th><th className="r">Rule traded</th><th>Status</th>
        </tr></thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.id} data-testid={`trend-month-${m.id}`}>
              <td className="num">{m.entry_date}</td>
              <td className="num">{m.exit_date || "open"}</td>
              <td className="id">{(m.held || []).length ? m.held.join(", ") : "none"}</td>
              <td className="num">{isNum(m.weights?.BIL) ? `${Math.round(Number(m.weights.BIL) * 100)}%` : "n/a"}</td>
              <td className="num">{pctN(m.rule_net_ret, 2, "pending")}</td>
              <td className="num">{pctN(m.bench_net_ret, 2, "pending")}</td>
              <td className="num" style={{ color: "var(--text-primary)" }}>{pctN(m.excess, 2, "pending")}</td>
              <td className="num">{isNum(m.rule_turnover) ? `${(Number(m.rule_turnover) * 100).toFixed(0)}%` : "pending"}</td>
              <td className="sn-small">{m.status_text || m.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TrendPanel() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["eq-trend"], queryFn: api.eqTrend, refetchInterval: eqPoll(300000) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["eq-trend"] });
  const toggle = useMutation({
    mutationFn: (enabled) => api.eqTrendConfig({ enabled }),
    onSuccess: (c) => { toast.success(c.enabled ? "Paper tracking is on." : "Paper tracking is paused."); refresh(); },
    onError: (e) => toast.error(`The setting was not saved. ${trendErr(e)}`),
  });
  const runNow = useMutation({
    mutationFn: () => api.eqTrendRun(),
    onSuccess: (r) => {
      const did = (r.did || []).length ? r.did.join(", ") : "nothing to do today";
      if (r.errors?.length) toast.error(`Run for ${r.date}: ${r.errors.join("; ")}`);
      else toast.success(`Run for ${r.date}: ${r.note || did}.`);
      refresh();
    },
    onError: (e) => toast.error(`The run did not start. ${trendErr(e)}`),
  });
  const s = data?.summary || {};
  const n = s.months_closed ?? 0;
  const target = s.target_months ?? 12;
  const rule = s.rule;
  const bench = s.bench;
  const enabled = data?.enabled !== false;
  const last = data?.last_result;
  return (
    <Panel title="Trend following, five funds (paper)" icon="TrendingUp" data-testid="trend-panel"
           actions={can("owner") && (
             <span className="sn-row" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
               <Switch label="Paper tracking" checked={enabled} disabled={toggle.isPending}
                       onChange={(on) => toggle.mutate(on)} data-testid="trend-toggle" />
               <Button size="sm" variant="secondary" icon="RefreshCw" loading={runNow.isPending}
                       onClick={() => runNow.mutate()} data-testid="trend-run-now">Run now</Button>
             </span>
           )}>
      {isLoading ? <Skeleton lines={4} /> : (
        <div className="sn-stack">
          <p className="sn-small sn-muted" style={{ margin: 0 }}>
            On the first trading day of each month, each of five funds (SPY, EFA, IEF, VNQ, DBC) is held for the month when its
            month-end close is above the average of its last ten month-end closes, and otherwise its fifth sits in Treasury bills (BIL);
            the benchmark holds all five, equal weights, rebalanced monthly, and both pay 0.05% on what they trade. On 2013 to 2026 the
            rule had a third of the benchmark&apos;s worst drawdown for about one point a year less return (research/trend_following/RESULTS.md);
            it is judged once after twelve months on paper, and nothing here is a signal.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="trend-summary">
            <StatCell size="sm" label="Months closed" value={`${n} of ${target}`}
                      sub={s.verdict ? `judged: ${s.verdict}` : n === 0 ? "first opens 2 November 2026" : `${s.months_remaining ?? target - n} to go`} />
            <StatCell size="sm" label="Rule, return a year" value={pctN(rule?.annual_return)}
                      sub={rule ? "annualised, after costs" : "no paper month closed yet"} />
            <StatCell size="sm" label="Rule, worst drawdown" value={pctN(rule?.max_drawdown)}
                      sub={rule ? "peak to trough on month ends" : "no paper month closed yet"} />
            <StatCell size="sm" label="Benchmark, return a year" value={pctN(bench?.annual_return)} sub="five funds, equal weight, always held" />
            <StatCell size="sm" label="Benchmark, worst drawdown" value={pctN(bench?.max_drawdown)}
                      sub={isNum(s.drawdown_ratio) ? `the rule's is ${Math.round(Number(s.drawdown_ratio) * 100)}% of it` : "same funds, same costs"} />
          </div>
          {n === 0 && (
            <p className="sn-small" style={{ margin: 0 }} data-testid="trend-empty">
              No paper month closed yet; the first opens on 2 November 2026.
            </p>
          )}
          {s.verdict && (
            <p className="sn-small" style={{ margin: 0 }} data-testid="trend-verdict">
              <strong>{s.verdict}</strong> at {target} months: the rule&apos;s worst drawdown is
              {" "}{isNum(s.drawdown_ratio) ? `${Math.round(Number(s.drawdown_ratio) * 100)}%` : "n/a"} of the benchmark&apos;s and its return is
              {" "}{isNum(s.return_gap) ? `${(Number(s.return_gap) * 100).toFixed(1)} points a year` : "n/a"} below it. Confirmed needs a drawdown of at
              most two-thirds and a gap of at most 2 points; a gap of 2 to 4 points is consistent.
            </p>
          )}
          <TrendSignals signals={data?.latest_signals} asOf={data?.signals_as_of} source={data?.signals_source} />
          <TrendMonths months={data?.months || []} />
          <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="trend-status">
            Tracking is {enabled ? "on" : "paused"}.
            {data?.last_run_at ? ` Last run ${etSyd(data.last_run_at, { seconds: false })}` : " It has not run yet"}
            {last?.date ? ` for ${last.date}` : ""}
            {last?.did?.length ? `: ${last.did.join(", ")}` : ""}.
            {last?.errors?.length ? ` Problems: ${last.errors.join("; ")}.` : ""}
            {" "}Positions are taken at the close of the first session and held to the close of the next month&apos;s first session.
            Returns use official closes plus dividends with an ex-date after entry and on or before exit, after 0.05% of the value traded.
          </p>
        </div>
      )}
    </Panel>
  );
}

// ── historical replay ────────────────────────────────────────────────────────
const RUN_PHASE = {
  queued: "Queued", caching_bars: "Caching daily bars", caching_earnings: "Caching earnings history",
  caching_earnings_calendar: "Caching the earnings calendar (one day per call)",
  caching_symbol_earnings: "Caching each stock's earnings history", universe: "Building the daily universe",
  events: "Measuring event reactions", caching_grades: "Caching analyst grade history",
  loading: "Loading history", features: "Computing features", setups: "Replaying setups", done: "Done",
  formation_days: "Forming daily loser lists", caching_sectors: "Caching each stock's sector",
  daily_bars: "Fetching daily bars and dividends", econ_calendar: "Fetching the economic calendar",
  minute_bars: "Fetching one-minute bars",
};

function VariantTable({ variants }) {
  if (!variants?.length) return null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="replay-variants">
      <span className="sn-micro">Parameter variants (same period)</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="Replay variants">
          <thead><tr>
            <th>Variant</th><th>Changes</th><th className="r">Closed</th><th className="r">Win rate</th>
            <th className="r">Avg R</th><th className="r">Avg R, actionable only</th><th className="r">Avg R, later third</th>
          </tr></thead>
          <tbody>
            {variants.map((v) => (
              <tr key={v.name}>
                <td className="id">{v.name}</td>
                <td style={{ whiteSpace: "normal" }} className="sn-small">
                  {Object.keys(v.params || {}).length ? Object.entries(v.params).map(([k, x]) => `${k}=${x}`).join(", ") : "defaults"}
                </td>
                <td className="num">{v.overall?.closed}</td>
                <td className="num">{fmtRate(v.overall?.win_rate)}</td>
                <td className="num" style={{ color: "var(--text-primary)" }}>{fmtR(v.overall?.mean_r)}</td>
                <td className="num">{fmtR(v.actionable_only?.mean_r)}</td>
                <td className="num">{fmtR(v.out_of_sample?.mean_r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="sn-small sn-faint">
        A variant that only wins in the earlier two-thirds and not in the later third is fitting the past. Change the rules only if the later third agrees.
      </span>
    </div>
  );
}

const FILTER_LABEL = { F0: "no filter", F1: "score ≥ 55", F2: "< 2 ATR above EMA20", F3: "risk-on only", F4: "all three" };

function CellTable({ variants }) {
  const rows = [];
  for (const v of variants || []) {
    for (const [f, c] of Object.entries(v.by_filter || {})) rows.push({ cell: `${v.name}-${f}`, f, ...c });
  }
  if (!rows.length) return null;
  rows.sort((a, b) => (b.overall?.ci95?.[0] ?? -99) - (a.overall?.ci95?.[0] ?? -99));
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="replay-cells">
      <span className="sn-micro">Pre-registered cells (entry–exit–filter), best lower bound first</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label="Research cells">
          <thead><tr>
            <th>Cell</th><th>Filter</th><th className="r">Closed</th><th className="r">Avg R</th><th className="r">95% CI</th>
            <th className="r">Earlier ⅔</th><th className="r">Later ⅓</th><th>Eligible</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cell} data-testid={`cell-${r.cell}`}>
                <td className="id">{r.cell}</td>
                <td className="sn-small">{FILTER_LABEL[r.f] || r.f}</td>
                <td className="num">{r.overall?.closed}</td>
                <td className="num" style={{ color: "var(--text-primary)" }}>{fmtR(r.overall?.mean_r)}</td>
                <td className="num">{r.overall?.ci95 ? `${fmtR(r.overall.ci95[0])} … ${fmtR(r.overall.ci95[1])}` : "—"}</td>
                <td className="num">{fmtR(r.in_sample?.mean_r)}</td>
                <td className="num">{fmtR(r.out_of_sample?.mean_r)}</td>
                <td>{r.eligible ? <Badge icon="CircleCheck">Eligible</Badge> : <span className="sn-faint">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="sn-small sn-faint">
        Eligible = at least 150 closed trades and above zero in both the earlier two-thirds and the later third (research/equities_v3/PREREG.md).
      </span>
    </div>
  );
}

const fmtP = (v) => (isNum(v) ? `${v >= 0 ? "+" : "−"}${Math.abs(v * 100).toFixed(2)}%` : "—");

function PeadTable({ title, cells, testId }) {
  const rows = Object.entries(cells || {}).map(([k, c]) => ({ k, ...c }))
    .sort((a, b) => (b.overall?.ci95_adj?.[0] ?? -9) - (a.overall?.ci95_adj?.[0] ?? -9));
  if (!rows.length) return null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid={testId}>
      <span className="sn-micro">{title}</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label={title}>
          <thead><tr>
            <th>Cell</th><th className="r">Trades</th><th className="r">Avg return</th><th className="r">Avg vs benchmark</th>
            <th className="r">95% CI vs benchmark</th><th className="r">Beat benchmark</th><th className="r">Earlier ⅔ vs bm</th>
            <th className="r">Later ⅓ vs bm</th><th>Eligible</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k} data-testid={`pead-${r.k}`}>
                <td className="id">{r.k}</td>
                <td className="num">{r.overall?.trades}</td>
                <td className="num">{fmtP(r.overall?.mean_ret)}</td>
                <td className="num" style={{ color: "var(--text-primary)" }}>{fmtP(r.overall?.mean_adj)}</td>
                <td className="num">{r.overall?.ci95_adj ? `${fmtP(r.overall.ci95_adj[0])} … ${fmtP(r.overall.ci95_adj[1])}` : "—"}</td>
                <td className="num">{fmtRate(r.overall?.beat_spy)}</td>
                <td className="num">{fmtP(r.earlier?.mean_adj)}</td>
                <td className="num">{fmtP(r.later?.mean_adj)}</td>
                <td>{r.eligible ? <Badge icon="CircleCheck">Eligible</Badge> : <span className="sn-faint">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PeadDetail({ r }) {
  return (
    <div className="sn-stack" data-testid="pead-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Post-earnings drift · {r.period?.start} to {r.period?.end} (split {r.period?.split}) · {r.events_used} usable reactions
        of {r.events_in_range} reports. Signals S1–S3, holds H20/H40/H60/H20S (stop), filters F0 none / FM momentum ≥ 50 / FR risk-on.
        See research/equities_pead/PREREG.md.
      </p>
      <PeadTable title="Strategy cells vs SPY, best lower bound first" cells={r.cells} testId="pead-cells" />
      <PeadTable title="Controls (not selectable): C0 every report, C1 misses that fell" cells={r.controls} testId="pead-controls" />
    </div>
  );
}

function Drift2Detail({ r }) {
  const pick = (obj, p) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => k.startsWith(p)));
  const g = r.data_gate_B || {};
  return (
    <div className="sn-stack" data-testid="drift2-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Event drift round 2 · {r.period?.start} to {r.period?.end} (split {r.period?.split}). Returns are measured
        against the equal-weighted universe. Analyst grades: {g.symbols_with_grades_in_range ?? "—"} of {g.universe_symbols ?? "—"} universe
        stocks have grade changes in range; {g.upgrades_used ?? "—"} usable upgrades. See research/equities_drift2/PREREG.md.
      </p>
      <PeadTable title="Test A — earnings beats vs equal weight" cells={pick(r.cells, "A-")} testId="drift2-a" />
      <PeadTable title="Test B — analyst upgrades vs equal weight" cells={pick(r.cells, "B-")} testId="drift2-b" />
      <PeadTable title="Controls (not selectable): A-C0 every report, B-D1 downgrades" cells={r.controls} testId="drift2-controls" />
    </div>
  );
}

const fmtBp = (v) => (isNum(v) ? `${v >= 0 ? "+" : "−"}${Math.abs(v * 1e4).toFixed(1)} bp` : "—");
const fmtNum = (v, d = 2) => (isNum(v) ? Number(v).toFixed(d) : "—");

function ReversalDetail({ r }) {
  const g = r.data_gate || {};
  const pass = Object.entries(r.cells || {}).filter(([, c]) => c.holdout_pass !== undefined);
  return (
    <div className="sn-stack" data-testid="reversal-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        No-news short-term reversal · {r.period?.start} to {r.period?.end} (split {r.period?.split}). Buys the tenth of the
        universe that fell most against its peers, skipping stocks with an earnings report in or near the window; returns are
        after 0.05% per side and measured against the equal-weighted universe. A recent earnings report is on
        record for {fmtRate(g.earnings_coverage)} of stock-days (stocks without one are not traded); sector known for {fmtRate(g.sector_coverage)} of stock-days. See research/equities_reversal/PREREG.md.
      </p>
      {r.void && <p className="sn-small" data-testid="reversal-void">Void: {r.void_reason}. No cell was computed.</p>}
      {!r.void && !g.sector_ok && <p className="sn-small sn-muted">Sector coverage is below 85%, so the sector-relative (R1) cells were not tested.</p>}
      <PeadTable title="Cells (no-earnings filter), best lower bound first" cells={r.cells} testId="reversal-cells" />
      {r.finalists_by_rule && (
        <p className="sn-small" data-testid="reversal-finalists">
          Finalists by the pre-registered rule: {r.finalists_by_rule.length ? r.finalists_by_rule.join(", ") : "none (no eligible cell)"}.
        </p>
      )}
      {pass.map(([k, c]) => (
        <p className="sn-small" key={k} data-testid={`reversal-pass-${k}`}>
          {k}: {c.holdout_pass ? "PASS" : "No pass"} · mean vs benchmark − 2.24 × SE = {fmtP(c.holdout_bound)}
        </p>
      ))}
      <PeadTable title="Controls (not selectable): X0 no filter, XE reported earnings, W winners" cells={r.controls} testId="reversal-controls" />
    </div>
  );
}

function PerfTable({ title, rows, testId }) {
  const shown = (rows || []).filter(([, v]) => v && v.n != null);
  if (!shown.length) return null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid={testId}>
      <span className="sn-micro">{title}</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label={title}>
          <thead><tr>
            <th>Series</th><th className="r">Sessions / events</th><th className="r">Mean</th>
            <th className="r">Per year</th><th className="r">Sharpe</th><th className="r">t</th>
          </tr></thead>
          <tbody>
            {shown.map(([label, v]) => (
              <tr key={label}>
                <td className="id">{label}</td>
                <td className="num">{v.n}</td>
                <td className="num" style={{ color: "var(--text-primary)" }}>{fmtBp(v.mean_daily ?? v.mean)}</td>
                <td className="num">{isNum(v.ann_return) ? fmtP(v.ann_return) : "—"}</td>
                <td className="num">{fmtNum(v.sharpe)}</td>
                <td className="num">{fmtNum(v.t_nw ?? v.t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RebalDetail({ r }) {
  const rep = r.replication || {};
  const h = r.holdout;
  return (
    <div className="sn-stack" data-testid="rebal-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Rebalancing-flow signal, S&amp;P minus 10-year Treasuries (SPY minus IEF total returns), built as published. Data
        {" "}{r.data?.first} to {r.data?.last}. The unseen period opens only if the replication reaches a Sharpe ratio of 0.5 before
        costs. See research/rebalancing/PREREG.md.
      </p>
      <p className="sn-small" data-testid="rebal-verdict"><strong>{r.verdict}</strong>{isNum(h?.t_nw) ? ` · t = ${fmtNum(h.t_nw)} (needs 1.96)` : ""}</p>
      <PerfTable title={`Replication ${rep.window?.[0]} to ${rep.window?.[1]} (inside the paper's sample)`} testId="rebal-replication"
                 rows={[["Before costs", rep.gross], ["After costs", rep.net]]} />
      {h && (
        <PerfTable title={`Unseen period ${h.window?.[0]} to ${h.window?.[1]}`} testId="rebal-holdout"
                   rows={[["After costs (deciding)", h.net], ["Before costs", h.gross], ["Costs doubled", h.stress_2bp],
                          ["Threshold part only", h.threshold_part], ["Calendar part only", h.calendar_part],
                          ["Enter at the next open", h.next_open_variant],
                          ["High-VIX half", h.vix_split?.high], ["Low-VIX half", h.vix_split?.low]]} />
      )}
    </div>
  );
}

function CalendarDetail({ r }) {
  const { D1 = {}, D2 = {}, D3 = {} } = r;
  return (
    <div className="sn-stack" data-testid="calendar-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Three calendar effects tested in SPY, each needing t ≥ {r.threshold_t} (the 5% threshold split three ways).
        See research/calendar_underlying/PREREG.md.
      </p>
      <p className="sn-small" data-testid="cal-d1"><strong>D1 third-Friday reversal: {D1.verdict}</strong></p>
      <PerfTable title="D1 return per event (long overnight, then short open to close, after costs)" testId="cal-d1-table"
                 rows={[["Replication 2003–2021", D1.replication?.event_return], ["Test 2022 onward (deciding)", D1.test?.event_return],
                        ["Test: short leg only", D1.test?.short_leg_only], ["Test: short leg when overnight was up", D1.test?.short_leg_when_overnight_up],
                        ["Test: short open to noon", D1.test?.short_open_to_noon], ["Test: other Fridays, before costs", D1.test?.other_fridays_gross]]} />
      <p className="sn-small" data-testid="cal-d2">
        <strong>D2 announcement-day premium: {D2.verdict}</strong>
        {isNum(D2.event_mean) && ` · event days ${fmtBp(D2.event_mean)} (${D2.event_days}) vs other days ${fmtBp(D2.other_mean)} · difference ${fmtBp(D2.difference?.diff)}, t = ${fmtNum(D2.difference?.t)}`}
        {D2.years_dropped?.length ? ` · years dropped by the data gate: ${D2.years_dropped.join(", ")}` : ""}
      </p>
      <p className="sn-small" data-testid="cal-d3"><strong>D3 noise-band intraday momentum: {D3.verdict}</strong></p>
      <PerfTable title="D3 daily return, after costs" testId="cal-d3-table"
                 rows={[["Replication, published sizing", D3.replication?.published_sizing], ["Test (deciding)", D3.test?.published_sizing],
                        ["Test at 1× exposure", D3.test?.one_times], ["Test, costs doubled", D3.test?.stress_2bp]]} />
    </div>
  );
}

function ReplayDetail({ id }) {
  const { data } = useQuery({
    queryKey: ["eq-replay", id], queryFn: () => api.eqReplay(id),
    refetchInterval: (q) => (["QUEUED", "RUNNING"].includes(q.state.data?.status) ? 5000 : false),
  });
  if (!data) return <Skeleton lines={4} />;
  if (data.status !== "DONE") {
    return (
      <p className="sn-small sn-muted" data-testid="replay-progress">
        <Icon name="Hourglass" size={14} /> {data.status === "ERROR" ? `Failed: ${data.error}`
          : `${RUN_PHASE[data.progress?.phase] || data.progress?.phase || "Queued"}${data.progress?.total ? ` · ${data.progress.done}/${data.progress.total}` : ""}`}
      </p>
    );
  }
  const r = data.result || {};
  if (r.kind === "pead") return <PeadDetail r={r} />;
  if (r.kind === "drift2") return <Drift2Detail r={r} />;
  if (r.kind === "reversal") return <ReversalDetail r={r} />;
  if (r.kind === "rebal") return <RebalDetail r={r} />;
  if (r.kind === "calendar") return <CalendarDetail r={r} />;
  if (r.kind === "short") return (
    <div className="sn-stack" data-testid="short-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Hedged short after analyst downgrades · {r.period?.start} to {r.period?.end} (split {r.period?.split}).
        Short the stock, long the equal-weighted universe; returns are after 0.05% per side on each leg and 1%/yr borrow.
        Grades: {r.data_gate?.symbols_with_grades_in_range ?? "—"} of {r.data_gate?.universe_symbols ?? "—"} stocks;
        {" "}{r.data_gate?.downgrades_used ?? "—"} usable downgrades. See research/equities_short/PREREG.md.
      </p>
      <PeadTable title="Hedged short cells (return = hedged P&L)" cells={r.cells} testId="short-cells" />
    </div>
  );
  const b = r.baseline || {};
  return (
    <div className="sn-stack" data-testid="replay-detail">
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        {r.period?.start} to {r.period?.end} · {r.period?.sessions} sessions
        {r.period?.regime_sessions && ` (${Object.entries(r.period.regime_sessions).map(([k, v]) => `${BUCKET_LABEL[k] || k} ${v}`).join(", ")})`}
        {r.data && ` · ${r.data.symbols} symbols cached`}
      </p>
      <span className="sn-micro">All qualifying plans</span>
      <SummaryCells s={b.overall} testId="replay-overall" />
      <span className="sn-micro">Only plans the desk would have marked actionable (earnings clear, regime allows)</span>
      <SummaryCells s={b.actionable_only} testId="replay-actionable" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCell size="sm" label="Top 3 per day, actionable" value={fmtR(b.top3_per_day?.mean_r)} sub={`${b.top3_per_day?.closed ?? 0} trades`} />
        <StatCell size="sm" label={`Earlier two-thirds (to ${b.split_date || "—"})`} value={fmtR(b.in_sample?.mean_r)} sub={`${b.in_sample?.closed ?? 0} trades`} />
        <StatCell size="sm" label="Later third" value={fmtR(b.out_of_sample?.mean_r)} sub={`${b.out_of_sample?.closed ?? 0} trades`} />
      </div>
      <VariantTable variants={r.variants} />
      <CellTable variants={r.variants} />
      <BreakdownTable title="By year" rows={b.by_year} testId="replay-by_year" />
      <Breakdowns data={b} prefix="replay" />
      <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
        <span className="sn-micro">Limits of this replay</span>
        <ul className="sn-small sn-muted" style={{ margin: 0, paddingLeft: "var(--space-4)" }}>
          {(r.limitations || []).map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Replays() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["eq-replays"], queryFn: api.eqReplays,
    refetchInterval: (q) => ((q.state.data?.runs || []).some((r) => ["QUEUED", "RUNNING"].includes(r.status)) ? 5000 : false),
  });
  const { data: bars } = useQuery({ queryKey: ["eq-bars-status"], queryFn: api.eqBarsStatus });
  const runs = data?.runs || [];
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ start: "", end: "", preset: "standard" });
  const { data: presets } = useQuery({ queryKey: ["eq-replay-presets"], queryFn: api.eqReplayPresets });
  const [ask, setAsk] = useState(false);
  const current = sel || runs[0]?.id;
  const start = useMutation({
    mutationFn: () => api.eqStartReplay({ ...(form.start ? { start: form.start } : {}), ...(form.end ? { end: form.end } : {}),
                                          preset: form.preset }),
    onSuccess: (r) => { toast.success("Replay started. The first run caches years of bars and takes a while."); setSel(r.id);
      qc.invalidateQueries({ queryKey: ["eq-replays"] }); },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  return (
    <Panel title="Historical replay" icon="FlaskConical" data-testid="replays"
           actions={can("owner") && (
             <Button size="sm" variant="primary" icon="FlaskConical" onClick={() => setAsk(true)} data-testid="replay-start-btn"
                     disabled={runs.some((r) => ["QUEUED", "RUNNING"].includes(r.status))}>Run replay</Button>
           )}>
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          The same rules and exit policy run over past daily data, choosing each day's universe from the stocks that traded then
          (including ones that later delisted). Bar cache: {bars?.days_cached ?? 0} days
          {bars?.first_day ? ` (${bars.first_day} to ${bars.last_day})` : ""}.
        </p>
        {runs.length === 0 ? (
          <EmptyState icon="FlaskConical" title="No replays yet">
            {can("owner") ? "Run one to test the rules on about three years of history." : "An owner can run one."}
          </EmptyState>
        ) : (
          <>
            <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
              {runs.map((r) => (
                <Button key={r.id} size="sm" variant={r.id === current ? "secondary" : "ghost"} onClick={() => setSel(r.id)}
                        data-testid={`replay-run-${r.id}`}>
                  {r.preset && r.preset !== "standard" ? `${r.preset} · ` : ""}{r.start} → {r.end} · {r.status}{r.headline?.baseline?.mean_r != null ? ` · ${fmtR(r.headline.baseline.mean_r)}` : ""}
                </Button>
              ))}
            </div>
            {current && <ReplayDetail id={current} />}
          </>
        )}
      </div>
      {ask && (
        <Modal title="Run a historical replay" icon="FlaskConical" onClose={() => setAsk(false)} width={480}
               footer={<>
                 <Button variant="ghost" onClick={() => setAsk(false)}>Cancel</Button>
                 <Button variant="primary" onClick={() => { setAsk(false); start.mutate(); }} data-testid="replay-confirm">Start</Button>
               </>}>
          <div className="sn-stack">
            <p className="sn-small sn-muted" style={{ margin: 0 }}>
              Leave the dates empty for the three years ending about six weeks ago. Research presets with pre-registered periods ignore the dates. It runs in the background on the server and does not affect the SPX desk.
            </p>
            <Select label="Variant set" value={form.preset} onChange={(v) => setForm({ ...form, preset: v })}
                    options={Object.keys(presets?.presets || { standard: [] }).map((k) => ({
                      value: k, label: `${k} (${(presets?.presets?.[k] || []).length} variants)` }))} />
            <TextField label="Start (YYYY-MM-DD)" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="replay-start" />
            <TextField label="End (YYYY-MM-DD)" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="replay-end" />
          </div>
        </Modal>
      )}
    </Panel>
  );
}

const fmtDelay = (s) => (s == null ? "—" : s < 120 ? `${s}s` : s < 7200 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
const SOURCE_LABEL = { fmp_stock: "FMP news", fmp_press: "FMP press releases", massive: "Massive news" };

function NewsPilot() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["eq-news"], queryFn: api.eqNewsStatus, refetchInterval: 60000 });
  const toggle = useMutation({
    mutationFn: (enabled) => api.eqNewsConfig({ enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["eq-news"] }),
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  const cfg = data?.config || {};
  const pj = data?.projection || {};
  return (
    <Panel title="News pilot · Phase 0 (collecting news, no scoring, no trades)" icon="Newspaper" data-testid="news-pilot"
           actions={can("owner") && (
             <Button size="sm" variant="ghost" icon={cfg.enabled ? "PowerOff" : "Power"}
                     onClick={() => toggle.mutate(!cfg.enabled)} data-testid="news-toggle">
               {cfg.enabled ? "Pause" : "Resume"}
             </Button>
           )}>
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          Collecting company news for the {cfg.universe_size ?? "—"} most liquid stocks to measure volume and delay before any
          model scoring starts. Repeats are later stories on a stock that already had one in the past hour; they become
          background for the model, not new signals. Status: {cfg.enabled ? "running" : "paused"}
          {cfg.last_tick_at ? ` · last check ${etSyd(cfg.last_tick_at)}` : ""}. Plan: research/news_pilot/PLAN.md.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCell size="sm" label="Relevant items / trading day" value={pj.relevant_per_day != null ? String(pj.relevant_per_day) : "—"}
                    sub={pj.trading_days ? `${pj.trading_days} complete day${pj.trading_days === 1 ? "" : "s"}${pj.relevant_per_other_day != null ? ` · ${pj.relevant_per_other_day} on other days` : ""}` : "start-up day and today not counted"} />
          <StatCell size="sm" label="Projected model cost / month" value={pj.est_model_cost_per_month_usd != null ? fmt(pj.est_model_cost_per_month_usd, "usd") : "—"}
                    sub={`cap ${fmt(pj.cap_usd || 250, "usd")}${pj.note ? ` · ${pj.note}` : ""}`} />
          {Object.entries(cfg.health || {}).map(([src, h]) => (
            <StatCell key={src} size="sm" label={SOURCE_LABEL[src] || src} value={h.ok ? "OK" : "Error"}
                      sub={h.ok ? `${h.rows ?? 0} rows last check` : h.error} />
          ))}
        </div>
        {(data?.daily || []).length > 0 && (
          <div className="sn-table-wrap">
            <table className="sn-table" aria-label="News collected per day">
              <thead><tr>
                <th>Day (ET)</th><th className="r">Relevant</th><th className="r">Repeats</th><th className="r">Duplicates</th>
                <th className="r">Filtered out</th><th className="r">Median delay</th><th className="r">90% delay</th>
                <th>Delay by source (median / 90%)</th>
              </tr></thead>
              <tbody>
                {data.daily.map((d) => (
                  <tr key={d.day}>
                    <td className="num">{d.day}</td>
                    <td className="num" style={{ color: "var(--text-primary)" }}>{d.relevant}</td>
                    <td className="num">{d.repeats ?? 0}</td>
                    <td className="num">{d.duplicates}</td>
                    <td className="num">{d.excluded}</td>
                    <td className="num">{fmtDelay(d.delay_median_s)}</td>
                    <td className="num">{fmtDelay(d.delay_p90_s)}</td>
                    <td className="sn-small">
                      {Object.entries(d.delay_by_source || {}).map(([s, v]) =>
                        `${SOURCE_LABEL[s] || s} ${fmtDelay(v.median_s)} / ${fmtDelay(v.p90_s)} (${v.n})`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(data?.recent || []).length > 0 && (
          <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
            <span className="sn-micro">Latest relevant items</span>
            <div className="sn-table-wrap">
              <table className="sn-table" aria-label="Latest news items">
                <thead><tr><th>Received</th><th>Stock</th><th>Headline</th><th>Source</th><th className="r">Delay</th></tr></thead>
                <tbody>
                  {data.recent.map((n) => (
                    <tr key={n.id}>
                      <td className="num">{etSyd(n.received_at)}</td>
                      <td className="id">{(n.universe_symbols || []).join(", ")}</td>
                      <td style={{ whiteSpace: "normal" }}>
                        {n.url ? <a href={n.url} target="_blank" rel="noreferrer" className="sn-link">{n.title}</a> : n.title}
                        {(n.flags || []).includes("question_title") && <span className="sn-small sn-muted"> · question headline</span>}
                      </td>
                      <td className="sn-small">{n.publisher || SOURCE_LABEL[n.source] || n.source}</td>
                      <td className="num">{fmtDelay(n.delay_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {(data?.recent_excluded || []).length > 0 && (
          <details>
            <summary className="sn-micro" style={{ cursor: "pointer" }}>Latest filtered-out items (check nothing real is dropped)</summary>
            <div className="sn-table-wrap" style={{ marginTop: "var(--space-2)" }}>
              <table className="sn-table" aria-label="Latest filtered-out news items">
                <thead><tr><th>Received</th><th>Stock</th><th>Headline</th><th>Source</th><th>Reason</th></tr></thead>
                <tbody>
                  {data.recent_excluded.map((n) => (
                    <tr key={n.id}>
                      <td className="num">{etSyd(n.received_at)}</td>
                      <td className="id">{(n.universe_symbols || []).join(", ")}</td>
                      <td style={{ whiteSpace: "normal" }}>
                        {n.url ? <a href={n.url} target="_blank" rel="noreferrer" className="sn-link">{n.title}</a> : n.title}
                      </td>
                      <td className="sn-small">{n.publisher || SOURCE_LABEL[n.source] || n.source}</td>
                      <td className="sn-small">{n.excluded}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </Panel>
  );
}

export function EquitiesTrackRecord() {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-track-record">
      <LiveRecord />
      {/* Paper record of the trend rule (research/trend_following): a record beside the record, not a signal. */}
      <TrendPanel />
      <Replays />
      {/* Research pilot status: below the record it must not be confused with. */}
      <NewsPilot />
    </div>
  );
}

// ── journal ──────────────────────────────────────────────────────────────────
const EMPTY = { symbol: "", plan_id: "", entry_date: "", entry_price: "", shares: "", fees: "0", stop: "",
                exit_date: "", exit_price: "", notes: "" };

function JournalForm({ initial, plans, onClose, onSave, busy }) {
  const [f, setF] = useState(initial || EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const planOpts = [{ value: "", label: "No linked plan" },
    ...plans.map((p) => ({ value: p.id, label: `${p.symbol} · ${SETUP_LABEL[p.setup] || p.setup} · ${p.signal_date}` }))];
  const submit = () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    onSave({
      symbol: f.symbol.trim().toUpperCase(), plan_id: f.plan_id || null, entry_date: f.entry_date,
      entry_price: num(f.entry_price), shares: parseInt(f.shares, 10), fees: num(f.fees) || 0, stop: num(f.stop),
      exit_date: f.exit_date || null, exit_price: num(f.exit_price), notes: f.notes || null,
    });
  };
  return (
    <Modal title={initial?.id ? "Edit trade" : "Log a trade"} icon="BookOpen" onClose={onClose} width={560}
           footer={<>
             <Button variant="ghost" onClick={onClose}>Cancel</Button>
             <Button variant="primary" onClick={submit} loading={busy} data-testid="journal-save"
                     disabled={!f.symbol || !f.entry_date || !f.entry_price || !f.shares}>Save</Button>
           </>}>
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Symbol" value={f.symbol} onChange={set("symbol")} required data-testid="journal-symbol" />
        <Select label="Linked plan" options={planOpts} value={f.plan_id}
                onChange={(v) => {
                  const p = plans.find((x) => x.id === v);
                  setF({ ...f, plan_id: v, symbol: p ? p.symbol : f.symbol, stop: p && !f.stop ? String(p.stop) : f.stop });
                }} />
        <TextField label="Entry date" type="date" value={f.entry_date} onChange={set("entry_date")} required data-testid="journal-entry-date" />
        <TextField label="Entry price" type="number" numeric value={f.entry_price} onChange={set("entry_price")} required data-testid="journal-entry-price" />
        <TextField label="Shares" type="number" numeric value={f.shares} onChange={set("shares")} required data-testid="journal-shares" />
        <TextField label="Fees ($, total)" type="number" numeric value={f.fees} onChange={set("fees")} />
        <TextField label="Stop" type="number" numeric value={f.stop} onChange={set("stop")} hint="Used for R; defaults to the plan's stop" />
        <span />
        <TextField label="Exit date" type="date" value={f.exit_date} onChange={set("exit_date")} data-testid="journal-exit-date" />
        <TextField label="Exit price" type="number" numeric value={f.exit_price} onChange={set("exit_price")} data-testid="journal-exit-price" />
      </div>
      <div style={{ marginTop: "var(--space-3)" }}>
        <TextField label="Notes" value={f.notes} onChange={set("notes")} />
      </div>
    </Modal>
  );
}

export function EquitiesJournal() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["eq-journal"], queryFn: api.eqJournal });
  const { data: opps } = useQuery({ queryKey: ["eq-opportunities"], queryFn: api.eqOpportunities });
  const { data: rec } = useQuery({ queryKey: ["eq-track-record", "all"], queryFn: () => api.eqTrackRecord(null) });
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const plans = [...(opps?.plans || []), ...(rec?.recent || []).map((r) => ({ ...r, id: r.plan_id }))]
    .filter((p, i, a) => p.id && a.findIndex((x) => x.id === p.id) === i);
  const refresh = () => qc.invalidateQueries({ queryKey: ["eq-journal"] });
  const save = useMutation({
    mutationFn: (body) => (edit?.id ? api.eqJournalUpdate(edit.id, body) : api.eqJournalAdd(body)),
    onSuccess: () => { toast.success("Saved"); setEdit(null); refresh(); },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  const rows = data?.entries || [];
  const s = data?.summary;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-journal">
      <Panel title="Trade journal" icon="BookOpen"
             actions={can("analyst") && <Button size="sm" variant="primary" icon="Plus" onClick={() => setEdit({})} data-testid="journal-add">Log a trade</Button>}>
        <div className="sn-stack">
          <p className="sn-small sn-muted" style={{ margin: 0 }}>{data?.note || "Your actual fills, kept separate from the model's tracked outcomes."}</p>
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="journal-summary">
              <StatCell size="sm" label="Trades" value={String(s.entries)} sub={s.open ? `${s.open} open` : null} />
              <StatCell size="sm" label="Total P&L" value={fmt(s.total_pnl, "usd", { signed: true })} />
              <StatCell size="sm" label="Win rate" value={fmtRate(s.win_rate)} />
              <StatCell size="sm" label="Your avg R" value={fmtR(s.avg_r_actual)} />
              <StatCell size="sm" label="Model avg R, same plans" value={fmtR(s.avg_r_model_same_plans)} />
              <StatCell size="sm" label="Entry vs model" value={isNum(s.avg_entry_vs_model) ? `${s.avg_entry_vs_model >= 0 ? "+" : "−"}$${Math.abs(s.avg_entry_vs_model).toFixed(2)}` : "—"}
                        sub="per share; + = you paid more" />
            </div>
          )}
        </div>
      </Panel>
      {isLoading ? <Skeleton lines={4} /> : rows.length === 0 ? (
        <EmptyState icon="BookOpen" title="No trades logged">
          Log the trades you actually take. Linking one to its plan compares your fills with the model's.
        </EmptyState>
      ) : (
        <div className="sn-table-wrap" data-testid="journal-table">
          <table className="sn-table" aria-label="Trade journal">
            <thead><tr>
              <th>Symbol</th><th>Entry</th><th className="r">Shares</th><th>Exit</th><th className="r">P&L</th>
              <th className="r">Your R</th><th>Model</th><th>Notes</th><th />
            </tr></thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id} data-testid={`journal-row-${x.id}`}>
                  <td className="id">{x.symbol}</td>
                  <td className="num">{px(x.entry_price)} · {x.entry_date}</td>
                  <td className="num">{x.shares}</td>
                  <td className="num">{x.closed ? `${px(x.exit_price)} · ${x.exit_date}` : <Badge>Open</Badge>}</td>
                  <td className="num" style={{ color: "var(--text-primary)" }}>{x.pnl != null ? fmt(x.pnl, "usd", { signed: true }) : "—"}</td>
                  <td className="num">{fmtR(x.r_actual)}</td>
                  <td className="sn-small">{x.model ? `${x.model.status}${x.model.r_multiple != null ? ` · ${fmtR(x.model.r_multiple)}` : ""}` : "—"}</td>
                  <td className="sn-small" style={{ whiteSpace: "normal" }}>{x.notes || ""}</td>
                  <td>
                    {can("analyst") && (
                      <span className="sn-row" style={{ gap: 4, flexWrap: "nowrap" }}>
                        <Button size="sm" variant="ghost" icon="Pencil" onClick={() => setEdit({
                          ...EMPTY, ...Object.fromEntries(Object.entries(x).map(([k, v]) => [k, v == null ? "" : String(v)])),
                          id: x.id })} data-testid={`journal-edit-${x.id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" icon="X" onClick={() => setDel(x)} data-testid={`journal-del-${x.id}`}>Delete</Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <JournalForm initial={edit.id ? edit : null} plans={plans} busy={save.isPending}
                            onClose={() => setEdit(null)} onSave={(b) => save.mutate(b)} />}
      {del && (
        <ConfirmModal title="Delete this trade?" tone="alarm" confirmLabel="Delete" onClose={() => setDel(null)}
                      onConfirm={async () => { await api.eqJournalDelete(del.id); refresh(); }}>
          {del.symbol} · {px(del.entry_price)} on {del.entry_date}. This cannot be undone.
        </ConfirmModal>
      )}
    </div>
  );
}
